import { useQueryClient } from '@tanstack/react-query'
import React, { useEffect, useState } from 'react'
import toast from 'react-simple-toasts'

import { Button, Select } from 'slab'
import { toastOptions, toasttOptionsError } from 'utils'
import {
  getGetAdminGranularAllowanceConfigsQueryKey,
  usePatchAdminGranularAllowanceConfigsId,
  usePostAdminGranularAllowanceConfigs
} from 'utils/web_api'
import { type AdminGranularAllowanceConfig } from 'utils/web_api.schemas'

import StateSelectionSection from './StateSelectionSection'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '../../ui/sheet'

interface ConfigFormProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  stateCodes: string[]
  config?: AdminGranularAllowanceConfig | null
  mode: 'create' | 'edit'
}

function ConfigForm ({ open, onOpenChange, stateCodes, config, mode }: ConfigFormProps) {
  const [year, setYear] = useState<string>('')
  const [projectedStates, setProjectedStates] = useState<string[]>([])
  const [deliveredStates, setDeliveredStates] = useState<string[]>([])
  const [isSubmitting, setIsSubmitting] = useState(false)

  const queryClient = useQueryClient()

  useEffect(() => {
    if (open) {
      if (mode === 'edit' && config) {
        setYear(config.year.toString())
        setProjectedStates(config.projected_data_state_codes)
        setDeliveredStates(config.delivered_data_state_codes)
      } else {
        const nextYear = new Date().getFullYear() + 1
        setYear(nextYear.toString())
        setProjectedStates([])
        setDeliveredStates([])
      }
    }
  }, [open, config, mode])

  const createMutation = usePostAdminGranularAllowanceConfigs({
    mutation: {
      onSuccess: () => {
        toast('Configuration created successfully!', toastOptions)
        queryClient.invalidateQueries({ queryKey: getGetAdminGranularAllowanceConfigsQueryKey() })
        onOpenChange(false)
      },
      onError: (error: any) => {
        toast(`Failed to create config: ${error.message || 'Unknown error'}`, toasttOptionsError)
      },
      onSettled: () => {
        setIsSubmitting(false)
      }
    }
  })

  const updateMutation = usePatchAdminGranularAllowanceConfigsId({
    mutation: {
      onMutate: async ({ id, data }) => {
        // Cancel outgoing refetches so they don't overwrite our optimistic update
        await queryClient.cancelQueries({ queryKey: getGetAdminGranularAllowanceConfigsQueryKey() })

        // Snapshot the previous value
        const previousConfigs = queryClient.getQueryData(getGetAdminGranularAllowanceConfigsQueryKey())

        // Optimistically update to the new value
        queryClient.setQueryData(getGetAdminGranularAllowanceConfigsQueryKey(), (old: any) => {
          if (!old?.data) return old
          return {
            ...old,
            data: old.data.map((config: AdminGranularAllowanceConfig) =>
              config.id === id
                ? { ...config, ...data }
                : config
            )
          }
        })

        return { previousConfigs }
      },
      onError: (error: any, variables, context) => {
        // If the mutation fails, use the context returned from onMutate to roll back
        if (context?.previousConfigs) {
          queryClient.setQueryData(getGetAdminGranularAllowanceConfigsQueryKey(), context.previousConfigs)
        }
        toast(`Failed to update config: ${error.message || 'Unknown error'}`, toasttOptionsError)
      },
      onSuccess: () => {
        toast('Configuration updated successfully!', toastOptions)
        onOpenChange(false)
      },
      onSettled: () => {
        setIsSubmitting(false)
        // Refetch to ensure we have the latest data
        queryClient.invalidateQueries({ queryKey: getGetAdminGranularAllowanceConfigsQueryKey() })
      }
    }
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)

    if (mode === 'create') {
      createMutation.mutate({
        data: {
          year: parseInt(year),
          delivered_data_state_codes: deliveredStates,
          projected_data_state_codes: projectedStates,
        }
      })
    } else if (config) {
      updateMutation.mutate({
        id: config.id,
        data: {
          delivered_data_state_codes: deliveredStates,
          projected_data_state_codes: projectedStates,
        }
      })
    }
  }

  const availableForProjected = stateCodes.filter(state => !deliveredStates.includes(state))
  const availableForDelivered = stateCodes.filter(state => !projectedStates.includes(state))

  const totalStatesAssigned = projectedStates.length + deliveredStates.length
  const isFormValid = year !== '' && year !== undefined && totalStatesAssigned === stateCodes.length

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        className='h-screen flex flex-col'
        style={{ minWidth: '720px' }}
      >
        <div className='border-b border-gray-200 pb-4'>
          <SheetHeader>
            <SheetTitle className='text-left text-2xl font-normal mb-2'>
              {mode === 'create' ? 'Create New Configuration' : 'Edit Configuration'}
            </SheetTitle>
            <SheetDescription className='text-left'>
              {mode === 'create'
                ? 'Create a new granular allowance configuration by defining which states use projected vs delivered data.'
                : 'Edit the state assignments for this configuration. Only configurations in pending or failed status can be edited.'}
            </SheetDescription>
          </SheetHeader>
        </div>

        <div className='flex-1 overflow-y-auto pt-4'>
          <form onSubmit={handleSubmit} className='space-y-6'>
            {mode === 'create' && (
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-2'>
                  Plan Year
                </label>
                <Select
                  value={year}
                  onChange={(e) => setYear(e.target.value)}
                  placeholder='Select plan year'
                  className='w-full'
                >
                  <option value={new Date().getFullYear().toString()}>
                    {new Date().getFullYear()}
                  </option>
                  <option value={(new Date().getFullYear() + 1).toString()}>
                    {new Date().getFullYear() + 1}
                  </option>
                </Select>
              </div>
            )}

            <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
              <StateSelectionSection
                title='Projected Data States'
                description='States that will use projected (previous year) pricing data'
                type='projected'
                selectedStates={projectedStates}
                setSelectedStates={setProjectedStates}
                availableStates={availableForProjected}
              />

              <StateSelectionSection
                title='Delivered Data States'
                description='States that will use delivered (current year) pricing data'
                type='delivered'
                selectedStates={deliveredStates}
                setSelectedStates={setDeliveredStates}
                availableStates={availableForDelivered}
              />
            </div>

            <div className='bg-gray-50 p-4 rounded-lg space-y-2' data-testid='coverage-summary'>
              <p className='text-sm font-medium text-gray-700'>Coverage Summary</p>
              <div className='grid grid-cols-1 gap-2 text-sm text-gray-600'>
                <div className='flex justify-between'>
                  <span>Projected states:</span>
                  <span className='font-medium'>{projectedStates.length} / {stateCodes.length}</span>
                </div>
                <div className='flex justify-between'>
                  <span>Delivered states:</span>
                  <span className='font-medium'>{deliveredStates.length} / {stateCodes.length}</span>
                </div>
                <div className='flex justify-between border-t border-gray-200 pt-2' data-testid='total-coverage'>
                  <span className='font-medium'>Total coverage:</span>
                  <span className='font-medium'>{projectedStates.length + deliveredStates.length} / {stateCodes.length}</span>
                </div>
                <div className='pt-2'>
                  {projectedStates.length + deliveredStates.length < stateCodes.length && (
                    <div className='text-red-600 text-xs bg-red-50 p-2 rounded'>
                      ⚠️ Missing {stateCodes.length - (projectedStates.length + deliveredStates.length)} states - all states must be assigned
                    </div>
                  )}
                  {projectedStates.length + deliveredStates.length === stateCodes.length && (
                    <div className='text-green-600 text-xs bg-green-50 p-2 rounded'>
                      ✅ All states covered
                    </div>
                  )}
                </div>
              </div>
            </div>
          </form>
        </div>

        <SheetFooter className='border-t border-gray-200 pt-4'>
          <Button type='button' variant='primary-destructive' onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!isFormValid || isSubmitting}
          >
            {isSubmitting
              ? (mode === 'create' ? 'Creating...' : 'Updating...')
              : (mode === 'create' ? 'Create Configuration' : 'Update Configuration')}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}

export default ConfigForm
